import React from 'react'
import PropTypes from 'prop-types'
import { Row, H2, Button, Subheading } from '@fs/zion-ui'
import { ThingCalendar } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import {
  DISABLED_BACKGROUND_COLOR,
  DISABLED_FOREGROUND_COLOR,
  REVIEWED_COLOR,
  IN_PROGRESS_COLOR,
} from '../../shared/utils/panelConstants'

/**
 * EventsCardHeader
 *
 * Header for the Events card. Displays an icon that changes color based on
 * review/edit state, the title and optional subheading, and an Edit button
 * when the card is reviewed and not currently in edit mode.
 *
 * @param {Object} props
 * @param {Function} props.setIsEditing - Function to toggle edit mode (accepts a boolean).
 * @param {boolean} props.editMode - Whether the card is currently in edit mode.
 * @param {boolean} [props.reviewed=false] - Whether the card has been reviewed. Changes icon color and button visibility.
 * @returns {JSX.Element} The header element for the events card.
 */
function EventsCardHeader({ setIsEditing, editMode, reviewed = false, disableEdit = false }) {
  const [t] = useTranslation()
  let iconColor = DISABLED_BACKGROUND_COLOR
  if (reviewed && !editMode) {
    iconColor = REVIEWED_COLOR
  }
  if (editMode) {
    iconColor = IN_PROGRESS_COLOR
  }
  return (
    <Row gutters="xxs" wrap>
      <ThingCalendar
        size="sm"
        background={iconColor}
        color={!editMode && !reviewed ? DISABLED_FOREGROUND_COLOR : undefined}
      />
      <H2 growX size="H5" alignY="middle">
        {t('events-card.title', 'Events')}
        {(!reviewed || editMode) && (
          <Subheading>{t('events-card.subheading', 'Birth, Death, Residence, and More')}</Subheading>
        )}
      </H2>

      {reviewed && !editMode && (
        <Button noShrinkX onClick={() => setIsEditing(true)} linkName="events-edit-event" disabled={disableEdit}>
          {t('events-card.edit.btn', 'Edit')}{' '}
        </Button>
      )}
    </Row>
  )
}

EventsCardHeader.propTypes = {
  setIsEditing: PropTypes.func.isRequired,
  editMode: PropTypes.bool.isRequired,
  reviewed: PropTypes.bool,
}

export default EventsCardHeader
